import { HelpPathOutlineIcon } from '@ikyucom/design-system-icons-react'
import { useCallback, useEffect, useId } from 'react'
import { CardBrandList } from '~/components/card/CardBrandList'
import { normalizeCardBrand } from '~/lib/reservationInput/card'
import { useIsPC } from '~/lib/ua'
import { getHelpCancelGuardUrl, getHelpSecurityCodeUrl } from '~/lib/url'
import { useReserveNavigate } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { Button } from '~/ui'
import { Section } from '../sd/Section'
import {
  useCardCvcError,
  useCardExpiryError,
  useCardHolder,
  useCardHolderError,
  useCardNumberError,
  useInitPayjpObject,
  usePayjpPublicKey,
  useSetCardHolder,
} from '../store/cancelGuard'
import { useSelectedCardStatus, useSetSelectedCardStatus } from '../store/card'
import type { SelectedCardInfo } from '../store/payment'
import { useSelectedCardInfo } from '../store/payment'
import { useCards } from '../store/stateMachine/state'
import { CardBrand } from './CardFusuma'
import { maskedNumberDisplay, useIsCancelGuardPlan } from './cancel-guard'

export function CancelGuard() {
  const isCancelGuardPlan = useIsCancelGuardPlan()
  const isPC = useIsPC()
  if (!isCancelGuardPlan) {
    return null
  }
  const CancelGuard = isPC ? PCCancelGuard : SPCancelGuard
  return <CancelGuard />
}

function PCCancelGuard() {
  const cards = useCards()
  return (
    <div className={css({ bg: 'white', p: '6', spaceY: '3' })}>
      <h2 className={css({ fontSize: '2xl', fontWeight: 'bold' })}>
        キャンセル料のお支払い
        {cards.length === 0 && (
          <span className={css({ ml: '1', fontSize: 'sm', color: 'accent.pink' })}>*</span>
        )}
      </h2>
      <div className={css({ spaceY: '5' })}>
        <CancelGuardBody />
      </div>
    </div>
  )
}

function SPCancelGuard() {
  const cards = useCards()
  return (
    <Section
      title={
        <div>
          キャンセル料のお支払い
          {cards.length === 0 && (
            <span className={css({ ml: '1', fontSize: 'sm', color: 'accent.pink' })}>*</span>
          )}
        </div>
      }
    >
      <CancelGuardBody />
    </Section>
  )
}

function CancelGuardBody() {
  const cards = useCards()
  const card = useSelectedCardInfo()
  const navigate = useReserveNavigate()
  const openCancelPolicy = useCallback(() => {
    navigate({ type: 'showCancelPolicy' })
  }, [navigate])

  return (
    <div className={css({ spaceY: { base: '4', _pc: '5' } })}>
      <div className={css({ bgColor: 'gray.100', borderRadius: 'default', p: '3' })}>
        ご予約の取り消しによりキャンセル料が発生した場合にのみ、クレジットカードにてキャンセル料を決済します。
        <button
          type="button"
          className={css({ display: 'block', color: 'link', pt: '2' })}
          onClick={openCancelPolicy}
        >
          キャンセルポリシーはこちら
        </button>
      </div>
      {cards.length > 0 ? <SelectedCard card={card} /> : <NewCard />}
    </div>
  )
}

function SelectedCard({ card }: { card: SelectedCardInfo }) {
  const isPC = useIsPC()
  const navigate = useReserveNavigate()
  const open = useCallback(() => {
    navigate({ type: 'showCreditCard' })
  }, [navigate])

  if (card === undefined) {
    return null
  }

  const styles = sva({
    slots: ['container', 'maskedNumber', 'holderName'],
    base: {
      container: {
        width: 'full',
        border: 'medium',
        borderRadius: 'default',
        p: { base: '3', _pc: '4' },
      },
      maskedNumber: {
        fontSize: { base: 'sm', _pc: 'md' },
      },
      holderName: {
        fontSize: { base: 'sm', _pc: 'md' },
      },
    },
  })
  const classes = styles()
  const buttonSize = isPC ? 'sm' : 'xs'

  return (
    <div className={classes.container}>
      <div
        className={css({
          display: 'flex',
          justifyItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <div className={css({ flex: '1' })}>
          <div className={css({ display: 'flex', alignItems: 'center', columnGap: '1' })}>
            <CardBrand brand={normalizeCardBrand(card.brand)} />
            <p className={classes.maskedNumber}>{maskedNumberDisplay(card.maskedNumber)}</p>
          </div>
          <p className={classes.holderName}>
            {card.holderName}（{card.goodThru}）
          </p>
        </div>
        <div>
          <Button color="tertiary" onClick={open} size={buttonSize}>
            変更
          </Button>
        </div>
      </div>
    </div>
  )
}

function NewCard() {
  const setSelectedCardStatus = useSetSelectedCardStatus()
  useEffect(() => {
    setSelectedCardStatus({ type: 'new' })
  }, [])

  return <PayjpNewCardInput />
}

export function PayjpNewCard() {
  const selected = useSelectedCardStatus()
  if (selected?.type !== 'new') {
    return null
  }

  return <PayjpNewCardInput />
}

/** キャンセルガード用クレジットカードの新規登録フォーム */
// 決済代行会社は PAY.JP を利用している。
// PCI-DSS 準拠のため、カード番号・有効期限(月/年)・CVC の入力フォームは payjp.js v2 を利用して生成しなければならない。
// 入力値は payjp.js v2 によりセキュアに処理され、macaron側には保持されない。
// カード名義の入力フォームは payjp.js v2 から提供されていないため macaron 側で用意する。
// https://pay.jp/docs/payjs
function PayjpNewCardInput() {
  const payjpPublicKey = usePayjpPublicKey()
  const initPayjpObject = useInitPayjpObject()
  const cardNumberError = useCardNumberError()
  const cardExpiryError = useCardExpiryError()
  const cardCvcError = useCardCvcError()
  const id = useId()
  const cardNumberId = `${id}-card-number`
  const cardExpiryId = `${id}-card-expiry`
  const cardCvcId = `${id}-card-cvc`

  useEffect(() => {
    if (window.Payjp) {
      // Payjpインスタンスは一度しか生成できないため window に保持する
      if (!window.PayjpInstance) {
        window.PayjpInstance = window.Payjp(payjpPublicKey)
      }
      initPayjpObject(window.PayjpInstance, cardNumberId, cardExpiryId, cardCvcId)
    }
  }, [payjpPublicKey, initPayjpObject, cardNumberId, cardExpiryId, cardCvcId])

  const styles = sva({
    slots: ['container', 'bottom', 'inputContainer'],
    base: {
      container: {
        width: 'full',
        borderRadius: 'default',
        spaceY: { base: '3', _pc: '4' },
      },
      bottom: {
        display: 'grid',
        gridTemplateColumns: '2',
        columnGap: '4',
      },
      inputContainer: {
        border: 'deep',
        borderRadius: 'default',
        padding: '3',
        width: 'full',
        userSelect: 'none',
      },
    },
  })
  const classes = styles()

  return (
    <div className={classes.container}>
      <Brands />
      <div className={css({ spaceY: '2' })}>
        <p className={css({ fontWeight: 'bold' })}>カード番号</p>
        <div id={cardNumberId} className={classes.inputContainer}></div>
        <Error error={cardNumberError} />
      </div>
      <CardHolderInput className={classes.inputContainer} />
      <div className={classes.bottom}>
        <div className={css({ spaceY: '2' })}>
          <p className={css({ fontWeight: 'bold' })}>有効期限（月/年）</p>
          <div id={cardExpiryId} className={classes.inputContainer}></div>
          <Error error={cardExpiryError} />
        </div>
        <div className={css({ spaceY: '2' })}>
          <div className={css({ display: 'flex', alignItems: 'center', columnGap: '1' })}>
            <p className={css({ fontWeight: 'bold' })}>セキュリティコード</p>
            <a
              href={getHelpSecurityCodeUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="セキュリティコードとは"
              tabIndex={-1}
            >
              <HelpPathOutlineIcon className={css({ color: 'gray.700' })} />
            </a>
          </div>
          <div id={cardCvcId} className={classes.inputContainer}></div>
          <Error error={cardCvcError} />
        </div>
      </div>
    </div>
  )
}

function Brands() {
  return (
    <div>
      <CardBrandList />
      <div className={css({ display: 'flex', alignItems: 'center', columnGap: '1' })}>
        <p className={css({ color: 'gray.700', fontSize: 'xs', pt: '1' })}>
          ※ デビットカードやプリペイドカードのご利用は非推奨です
        </p>
        <a
          href={getHelpCancelGuardUrl()}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="「キャンセル料のみカード決済」のプランでデビットカード・プリペイドカードが非推奨なのはなぜですか？"
        >
          <HelpPathOutlineIcon className={css({ color: 'gray.700' })} />
        </a>
      </div>
    </div>
  )
}

function CardHolderInput({
  className,
}: {
  className?: string
}) {
  const cardHolder = useCardHolder()
  const cardHolderError = useCardHolderError()
  const setCardHolder = useSetCardHolder()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCardHolder(e.target.value)
    },
    [setCardHolder]
  )

  return (
    <div className={css({ spaceY: '2' })}>
      <p className={css({ fontWeight: 'bold' })}>カード名義</p>
      <div className={className}>
        <input
          value={cardHolder ?? ''}
          placeholder="TARO IKYU"
          onChange={onChange}
          onBlur={onChange}
          className={css({
            border: 'none',
            outline: 'none',
            p: '0',
            fontSize: 'md',
            width: 'full',
            _placeholder: { color: 'gray.400!' },
          })}
        />
      </div>
      <Error error={cardHolderError} />
    </div>
  )
}

function Error({ error }: { error?: string }) {
  return (
    error !== undefined && (
      <p className={css({ mt: '1', fontSize: 'xs', color: 'error.text' })}>{error}</p>
    )
  )
}
